import React, { useState } from "react";
import { useLanguage, useT } from "@/lib/LanguageContext";
import { fmtCount, fmtMonthYear } from "../format";
import useCrmMoney from "../hooks/useCrmMoney";
import useRole from "../hooks/useRole";
import PackBalanceBadge from "../ui/PackBalanceBadge";
import CrmButton from "../ui/CrmButton";
import AssignPackDialog from "./AssignPackDialog";
import { CRM } from "../../../constants/testIds/crm";

/** One label/value pair of the strip. */
function Item({ label, children, testId }) {
  return (
    <div data-testid={testId} className="min-w-0">
      <dt className="text-sm text-eh-ink">{label}</dt>
      <dd className="mt-1 text-lg font-semibold text-eh-ink">{children}</dd>
    </div>
  );
}

/**
 * The read-only summary strip (spec): lifetime spend, sessions with the online /
 * offline split, the active pack ("6 of 10 used") with "Assign pack", courses owned,
 * products bought, member since.
 */
export default function SummaryStrip({ client, onChanged }) {
  const t = useT("crm");
  const { lang } = useLanguage();
  const money = useCrmMoney();
  const { can } = useRole();
  const [assigning, setAssigning] = useState(false);
  const s = client.summary || {};
  return (
    <section aria-label={t("detail.summary")} className="rounded-2xl border border-eh-border-soft bg-eh-cream p-5">
      <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 lg:grid-cols-5">
        <Item label={t("summary.spend")} testId={CRM.clientSummary("spend")}>{money(s.lifetime_spend_cents)}</Item>
        <Item label={t("summary.sessions")} testId={CRM.clientSummary("sessions")}>
          {fmtCount(s.sessions_total, lang)}{" "}
          <span className="text-sm font-normal">{t("summary.split", { online: fmtCount(s.sessions_online, lang), offline: fmtCount(s.sessions_offline, lang) })}</span>
        </Item>
        <Item label={t("summary.pack")} testId={CRM.clientSummary("pack")}>
          {s.active_pack ? (
            <>
              <span className="block text-sm font-normal text-eh-muted">{s.active_pack.pack_name}</span>
              <PackBalanceBadge pack={s.active_pack} size="lg" />
            </>
          ) : "—"}
          {can("assignPack") && (
            <CrmButton variant="link" size="sm" className="mt-1 block" onClick={() => setAssigning(true)} data-testid={CRM.assignPack}>
              {t("summary.assignPack")}
            </CrmButton>
          )}
        </Item>
        <Item label={t("summary.courses")} testId={CRM.clientSummary("courses")}>{fmtCount(s.courses_owned, lang)}</Item>
        <Item label={t("summary.products")} testId={CRM.clientSummary("products")}>{fmtCount(s.products_bought, lang)}</Item>
        <Item label={t("summary.memberSince")} testId={CRM.clientSummary("member_since")}>{s.member_since ? fmtMonthYear(s.member_since, lang) : "—"}</Item>
      </dl>
      {assigning && <AssignPackDialog client={client} onClose={() => setAssigning(false)} onAssigned={onChanged} />}
    </section>
  );
}
